import { useCallback, useEffect, useState } from 'react';
import { Badge, Stack, VStack, Text, Spacer, Flex, Icon, IconButton } from '@chakra-ui/react';
import { XMarkIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { toaster } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import api from '@/utils/api';
import { useModalValueToggle } from '@/lib/hooks/use-modal-toggle';
import { SortableList } from '@/components/sortable-list';
import EditOptionModal from './edit-option-modal';
import { FieldOption } from '@/lib/types';

interface Props {
  fieldId: number;
}

export default function CurrentOptions({ fieldId }: Props) {
  const editModal = useModalValueToggle<FieldOption>();
  const [options, setOptions] = useState<FieldOption[]>([]);

  const fetchOptions = useCallback(async () => {
    try {
      const response = await api.get(`fields/${fieldId}/options`);
      if (response.data.data) {
        const sortedOptions = response.data.data.sort(
          (a: FieldOption, b: FieldOption) => a.sort_order - b.sort_order,
        );
        setOptions(sortedOptions);
      }
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch field',
      });
    }
  }, [fieldId]);

  async function onRemove(item: FieldOption) {
    try {
      await api.delete(`field-options/${item.id}`);
      fetchOptions();
      toaster.create({
        title: 'Field option was successfully removed',
        type: 'success',
      });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to delete field option',
      });
    }
  }

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  async function handleSortEnd(newItems: FieldOption[]) {
    setOptions(newItems);
    const body = newItems.map(function (item) {
      return { field_option_id: item.id };
    });
    await api.put(`field-options/sort-order`, body);
  }

  return (
    <VStack direction={'row'} gap={4} align={'stretch'}>
      {options.length > 0 && (
        <SortableList
          items={options}
          onChange={handleSortEnd}
          renderItem={(item: FieldOption) => (
            <SortableList.Item id={item.id}>
              <Stack key={`field-${item.id}`} direction={'row'}>
                <Text fontSize={'sm'} mt={2}>
                  {item.option_label}{' '}
                  <Badge borderRadius={6} fontSize={'11px'} px={2} textTransform={'initial'}>
                    (Value: {item.option_value})
                  </Badge>
                </Text>
                <Spacer />
                <Flex justifyContent={'center'}>
                  <Tooltip content={'Edit'}>
                    <IconButton
                      aria-label={'Edit'}
                      variant={'ghost'}
                      size={'sm'}
                      onClick={() => {
                        editModal.open(item);
                      }}
                    >
                      <Icon as={PencilSquareIcon} h={4} w={4} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip content={'Delete'}>
                    <IconButton
                      aria-label={'Delete'}
                      variant={'ghost'}
                      size={'sm'}
                      onClick={() => onRemove(item)}
                    >
                      <Icon as={XMarkIcon} h={4} w={4} />
                    </IconButton>
                  </Tooltip>
                  <SortableList.DragHandle />
                </Flex>
              </Stack>
            </SortableList.Item>
          )}
        />
      )}

      {editModal.state && (
        <EditOptionModal
          {...editModal.props}
          option={editModal.state}
          fieldId={fieldId}
          onSuccess={fetchOptions}
        />
      )}
    </VStack>
  );
}
